import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  DeleteRounded,
  PlaylistAddRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { BulkCreateProjectPositionWorkflowStep } from '../../../api/project-position-workflow-steps';
import { RootState } from '../../../app/store';
import ConfirmDeleteBox from '../../../components/ConfirmDeleteBox';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  ProjectPosition,
  getProjectPositionsAsync
} from '../ProjectPosition/projectPositionSlice';
import {
  ProjectPositionWorkflowStep,
  bulkCreateProjectPositionWorkflowStepsAsync,
  deleteProjectPositionWorkflowStepAsync
} from '../ProjectPositionWorkflowStep/projectPositionWorkflowStepSlice';
import {
  createWorkflowStepAsync,
  getWorkflowStepAsync,
  updateWorkflowStepAsync
} from './workflowStepSlice';

interface CreateEditWorkflowStep {
  id: number | null;
  name: string | null;
  order: number | null;
  isInternal: boolean;
  canStopWorkflow: boolean;
  isFinalApprover: boolean;
  isChecklist: boolean;
}

interface SelectedProjectPositionWorkflowStep {
  projectPositionWorkflowStepId: number;
  positionName: string;
}

export default function WorkflowStepCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: projectPositions } = useSelector(
    (state: RootState) => state.projectPositions
  );
  const { selectedWorkflowStep } = useSelector(
    (state: RootState) => state.workflowSteps
  );

  const params = useParams<{ workflowStepId: string }>();
  const workflowStepId = Number.parseInt(params.workflowStepId, 10);
  const isNewPage = !Boolean(workflowStepId);

  const [workflowStep, setWorkflowStep] = useState<CreateEditWorkflowStep>({
    id: null,
    name: null,
    order: null,
    isInternal: false,
    canStopWorkflow: false,
    isFinalApprover: false,
    isChecklist: false
  });
  const isEditing = Boolean(
    selectedWorkflowStep &&
      (selectedWorkflowStep.name !== workflowStep.name ||
        selectedWorkflowStep.order !==
          Number.parseInt(workflowStep.order?.toString() ?? '-1') ||
        selectedWorkflowStep.isInternal !== workflowStep.isInternal ||
        selectedWorkflowStep.canStopWorkflow !== workflowStep.canStopWorkflow ||
        selectedWorkflowStep.isFinalApprover !== workflowStep.isFinalApprover ||
        selectedWorkflowStep.isChecklist !== workflowStep.isChecklist)
  );

  const [selectedProjectPositions, setSelectedProjectPositions] = useState<
    ProjectPosition[]
  >([]);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [
    selectedProjectPositionWorkflowStep,
    setSelectedProjectPositionWorkflowStep
  ] = useState<SelectedProjectPositionWorkflowStep | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedWorkflowStep) {
      setWorkflowStep({
        ...workflowStep,
        id: selectedWorkflowStep.id,
        name: selectedWorkflowStep.name,
        order: selectedWorkflowStep.order,
        isInternal: selectedWorkflowStep.isInternal,
        canStopWorkflow: selectedWorkflowStep.canStopWorkflow,
        isFinalApprover: selectedWorkflowStep.isFinalApprover,
        isChecklist: selectedWorkflowStep.isChecklist
      });
    }
  }, [selectedWorkflowStep]);

  useEffect(() => {
    if (!isNewPage) {
      dispatch(getWorkflowStepAsync(workflowStepId));
      if (projectPositions.length === 0) dispatch(getProjectPositionsAsync());
    }
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;
    setWorkflowStep({
      ...workflowStep,
      [name]: value === '' ? null : value
    });
  }

  function handleChangeSwitch(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setWorkflowStep({
      ...workflowStep,
      [event.target.name]: event.target.checked
    });
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      if (workflowStep.name && workflowStep.order) {
        if (isNewPage) {
          // create workflow step
          const createWorkflowStep = {
            name: workflowStep.name,
            order: Number.parseInt(workflowStep.order.toString()),
            isInternal: workflowStep.isInternal,
            canStopWorkflow: workflowStep.canStopWorkflow,
            isFinalApprover: workflowStep.isFinalApprover,
            isChecklist: workflowStep.isChecklist
          };
          await dispatch(createWorkflowStepAsync(createWorkflowStep)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowStepCreateEdit.messages.createSuccess') as string
            })
          );
        } else {
          // update workflow step
          const updateWorkflowStep = {
            id: workflowStep.id,
            name: workflowStep.name,
            order: Number.parseInt(workflowStep.order.toString()),
            isInternal: workflowStep.isInternal,
            canStopWorkflow: workflowStep.canStopWorkflow,
            isFinalApprover: workflowStep.isFinalApprover,
            isChecklist: workflowStep.isChecklist
          };
          await dispatch(updateWorkflowStepAsync(updateWorkflowStep)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowStepCreateEdit.messages.updateSuccess') as string
            })
          );
        }
      } else {
        throw new Error(t('workflowStepCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  function onCancelPosition(): void {
    setSelectedProjectPositions([]);
  }

  async function onSubmitNewPosition(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      if (!isNewPage && selectedWorkflowStep) {
        const bulkCreateProjectPositionWorkflowSteps: BulkCreateProjectPositionWorkflowStep =
          {
            workflowStepId: selectedWorkflowStep.id,
            projectPositionIds: selectedProjectPositions.map(
              (projectPosition) => projectPosition.id
            )
          };
        await dispatch(
          bulkCreateProjectPositionWorkflowStepsAsync(
            bulkCreateProjectPositionWorkflowSteps
          )
        ).unwrap();
        dispatch(getWorkflowStepAsync(selectedWorkflowStep.id));
        dispatch(
          loadingSlice.actions.success({
            message: t('workflowStepCreateEdit.messages.positionCreateSuccess') as string
          })
        );
        setSelectedProjectPositions([]);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } else {
        throw new Error(t('workflowStepCreateEdit.errors.general') as string);
      }
    } catch {
      setLoading(false);
    }
  }

  function handleSelectProjectPosition(
    projectPositionWorkflowStep?: ProjectPositionWorkflowStep
  ): void {
    if (projectPositionWorkflowStep) {
      setSelectedProjectPositionWorkflowStep({
        projectPositionWorkflowStepId: projectPositionWorkflowStep.id,
        positionName: projectPositionWorkflowStep.projectPosition.name
      });
      setOpenDeleteDialog(true);
    } else {
      setOpenDeleteDialog(false);
    }
  }

  async function onDeleteProjectPositionWorkflowStep(): Promise<void> {
    try {
      setLoading(true);
      if (selectedWorkflowStep && selectedProjectPositionWorkflowStep) {
        await dispatch(
          deleteProjectPositionWorkflowStepAsync(
            selectedProjectPositionWorkflowStep.projectPositionWorkflowStepId
          )
        ).unwrap();
        dispatch(getWorkflowStepAsync(selectedWorkflowStep.id));
        setOpenDeleteDialog(false);
        setSelectedProjectPositions([]);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } else {
        throw new Error(t('workflowStepCreateEdit.errors.general') as string);
      }
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('workflowStepCreateEdit.breadcrumb.workflowStep'),
          masterDataRef: 'workflow-steps',
          masterDataSubName: selectedWorkflowStep?.name,
          masterDataSubRef: selectedWorkflowStep?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('workflowStepCreateEdit.title.new') : t('workflowStepCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('workflowStepCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <TextField
              className={classes.columnLeft}
              id="order"
              label={t('workflowStepCreateEdit.labels.order')}
              size="small"
              variant="outlined"
              name="order"
              type="number"
              fullWidth
              value={workflowStep.order ? workflowStep.order : ''}
              onChange={onChangeField}
              required
            />
            <TextField
              className={classes.columnRight}
              id="workflow-step-name"
              label={t('workflowStepCreateEdit.labels.name')}
              size="small"
              variant="outlined"
              name="name"
              fullWidth
              value={workflowStep.name ? workflowStep.name : ''}
              onChange={onChangeField}
              required
            />
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={workflowStep.isInternal}
                onChange={handleChangeSwitch}
                name="isInternal"
                color="primary"
              />
            }
            label={t('workflowStepCreateEdit.labels.encOnly')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={workflowStep.canStopWorkflow}
                onChange={handleChangeSwitch}
                name="canStopWorkflow"
                color="primary"
              />
            }
            label={t('workflowStepCreateEdit.labels.canStopWorkflow')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={workflowStep.isFinalApprover}
                onChange={handleChangeSwitch}
                name="isFinalApprover"
                color="primary"
              />
            }
            label={t('workflowStepCreateEdit.labels.finalApprover')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={workflowStep.isChecklist}
                onChange={handleChangeSwitch}
                name="isChecklist"
                color="primary"
              />
            }
            label={t('workflowStepCreateEdit.labels.qualityCheck')}
          />
          <Button
            className={classes.submitWorkflowButton}
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
            disabled={loading || !(isNewPage || isEditing)}
          >
            {isNewPage ? t('workflowStepCreateEdit.buttons.add') : t('workflowStepCreateEdit.buttons.update')}
          </Button>
        </Paper>
      </form>
      {!isNewPage && (
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('workflowStepCreateEdit.sections.position')}
          </Typography>
          <Divider className={classes.divider} />
          <form onSubmit={onSubmitNewPosition}>
            <Autocomplete
              className={classes.positionAutocomplete}
              id="project-position"
              size="small"
              multiple
              options={projectPositions.filter(
                (projectPosition) =>
                  selectedWorkflowStep &&
                  !selectedWorkflowStep.projectPositionWorkflowSteps.some(
                    ({ projectPositionId }) =>
                      projectPositionId === projectPosition.id
                  )
              )}
              getOptionLabel={(option) => option.name}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    variant="outlined"
                    size="small"
                    deleteIcon={<ClearRounded />}
                  />
                ))
              }
              value={selectedProjectPositions}
              getOptionSelected={(option, value) => option.id === value?.id}
              onChange={(event, newValue) =>
                setSelectedProjectPositions(newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={
                    selectedProjectPositions.length === 0
                      ? t('workflowStepCreateEdit.placeholders.addNewPosition') as string
                      : ''
                  }
                  required={selectedProjectPositions.length === 0}
                />
              )}
            />
            <Collapse in={selectedProjectPositions.length > 0}>
              <Box className={classes.submitPositionButton}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<PlaylistAddRounded />}
                  disabled={loading || isEditing}
                >
                  {t('workflowStepCreateEdit.buttons.addPosition')}
                </Button>
                <Button size="large" fullWidth onClick={onCancelPosition}>
                  {t('workflowStepCreateEdit.buttons.cancel')}
                </Button>
              </Box>
            </Collapse>
          </form>
          {selectedWorkflowStep &&
            selectedWorkflowStep.projectPositionWorkflowSteps.length > 0 &&
            selectedWorkflowStep.projectPositionWorkflowSteps.map(
              (projectPositionWorkflowStep) => {
                return (
                  <Paper
                    className={classes.paperProjectPosition}
                    variant="outlined"
                    key={projectPositionWorkflowStep.id}
                  >
                    <Typography>
                      {projectPositionWorkflowStep.projectPosition.name}
                    </Typography>
                    <Tooltip
                      title={
                        isEditing || selectedProjectPositions.length > 0
                          ? t('workflowStepCreateEdit.tooltips.cannotDelete') as string
                          : t('workflowStepCreateEdit.tooltips.delete') as string
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          edge="end"
                          disabled={
                            isEditing || selectedProjectPositions.length > 0
                          }
                          onClick={() =>
                            handleSelectProjectPosition(
                              projectPositionWorkflowStep
                            )
                          }
                        >
                          <DeleteRounded />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Paper>
                );
              }
            )}
          {selectedWorkflowStep &&
            selectedWorkflowStep.projectPositionWorkflowSteps.length === 0 && (
              <div className={classes.notFoundText}>
                {t('workflowStepCreateEdit.messages.noPositionFound')}
              </div>
            )}
        </Paper>
      )}
      {selectedWorkflowStep && selectedProjectPositionWorkflowStep && (
        <ConfirmDeleteBox
          open={openDeleteDialog}
          text={t('workflowStepCreateEdit.confirmDelete.title', {
            positionName: selectedProjectPositionWorkflowStep.positionName.toUpperCase()
          }) as string}
          important
          onDelete={onDeleteProjectPositionWorkflowStep}
          onClose={handleSelectProjectPosition}
        >
          <Alert className={classes.alert} severity="error" icon={false}>
            {t('workflowStepCreateEdit.confirmDelete.message', {
              positionName: selectedProjectPositionWorkflowStep.positionName.toLowerCase(),
              stepName: selectedWorkflowStep.name.toLowerCase()
            })}
          </Alert>
        </ConfirmDeleteBox>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    row: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    columnLeft: {
      width: '50%',
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginRight: 0
      }
    },
    columnRight: {
      width: '50%',
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginLeft: 0
      }
    },
    submitWorkflowButton: {
      marginTop: theme.spacing(1.5)
    },
    positionAutocomplete: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    submitPositionButton: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      },
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2)
    },
    paperProjectPosition: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0
      }
    },
    notFoundText: {
      textAlign: 'center',
      padding: theme.spacing(1.75, 2)
    },
    alert: {
      marginTop: theme.spacing(1)
    }
  })
);
